import { useColorScheme } from '@mui/joy/styles'
import { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'

import { Moon, Sun } from 'layouts/components/sidebar/icons'

interface ColorSchemeToggleProps {
    /** Optional extra Tailwind classes — used by callers that need responsive show/hide rules */
    className?: string
    id?: string
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
}

/**
 * Type-narrow helper for the View Transitions API.
 * `document.startViewTransition` is only present in Chromium ≥ 111 / Safari ≥ 18,
 * so older / Firefox-stable browsers fall back to an instant theme swap.
 */
type ViewTransitionDocument = Document & {
    startViewTransition?: (callback: () => void | Promise<void>) => { ready: Promise<void> }
}

/**
 * Light/dark mode switcher.
 *
 * Visual style is intentionally aligned with the sidebar's collapse pill in
 * `Sidebar.tsx`: 28×28 rounded-xl button, slate-200/-700 border, white/slate-800
 * surface. This way every "control pill" in the sidebar header looks identical.
 *
 * Renders a single icon (Sun in dark mode, Moon in light mode) — the icon swap
 * acts as the visual affordance for the action you're about to take, matching
 * the reference design in `new-agent-numeo-main`.
 *
 * Side-effect: keeps Tailwind's `<html class="dark">` in sync with MUI Joy's
 * `useColorScheme()` so dark-mode utility classes resolve correctly.
 */
export default function ColorSchemeToggle({ className = '', id = 'toggle-mode', onClick }: ColorSchemeToggleProps) {
    const { mode, setMode } = useColorScheme()
    const [mounted, setMounted] = useState(false)
    const buttonRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        setMounted(true)
    }, [])

    // Sync Tailwind's dark class with MUI Joy's color scheme
    useEffect(() => {
        if (!mounted) return

        if (mode === 'dark') {
            document.documentElement.classList.add('dark')
        } else {
            document.documentElement.classList.remove('dark')
        }
    }, [mode, mounted])

    const baseClasses =
        'flex h-7 w-7 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-200 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white'

    if (!mounted) {
        return <button type="button" id={id} disabled className={`${baseClasses} ${className}`} aria-label="Toggle color scheme" />
    }

    const isDark = mode === 'dark'

    /**
     * Trigger the circular reveal: capture the button's centre, ask the browser
     * for a view-transition snapshot, then animate the new (incoming-theme)
     * snapshot's clip-path from a 0px circle at the centre out to the furthest
     * viewport corner. Falls back to an instant swap on browsers that don't
     * support `document.startViewTransition` (Firefox stable, Safari < 18).
     */
    const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
        const nextMode = isDark ? 'light' : 'dark'
        const doc = document as ViewTransitionDocument
        const start = doc.startViewTransition?.bind(doc)
        const button = buttonRef.current
        const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

        if (!start || !button || prefersReducedMotion) {
            setMode(nextMode)
            onClick?.(event)
            return
        }

        const rect = button.getBoundingClientRect()
        const x = rect.left + rect.width / 2
        const y = rect.top + rect.height / 2
        // Furthest corner from the click point — radius needed to cover the whole viewport.
        const endRadius = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y))

        const transition = start(() => {
            // flushSync forces React + Joy's setMode to commit synchronously inside
            // the view-transition callback; otherwise the snapshot captures the
            // stale theme and the reveal animates from old → old.
            flushSync(() => {
                setMode(nextMode)
            })
        })

        transition.ready
            .then(() => {
                document.documentElement.animate(
                    {
                        clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${endRadius}px at ${x}px ${y}px)`],
                    },
                    {
                        duration: 500,
                        easing: 'ease-in-out',
                        pseudoElement: '::view-transition-new(root)',
                    }
                )
            })
            .catch(() => {
                // Animation can fail if another transition starts mid-flight; theme is
                // already swapped via flushSync, so silently ignore.
            })

        onClick?.(event)
    }

    return (
        <button
            ref={buttonRef}
            type="button"
            id={id}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            className={`${baseClasses} ${className}`}
            onClick={handleToggle}
        >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
        </button>
    )
}
